<template>
  <div class="school-search-page">
    <div class="container school-search-container">
      <div class="u-page-inner">
        <header class="u-page-header school-search-header">
          <h1 class="u-page-title">學校進階搜尋</h1>
          <div class="u-page-line"></div>
        </header>

        <transition name="school-search-bar">
          <div
            v-if="formCollapsed && hasResults"
            key="search-collapsed-bar"
            class="school-search-form-collapsed"
          >
            <p class="school-search-form-collapsed-summary">{{ formSummaryBrief }}</p>
            <button type="button" class="btn btn-expand-form" @click="expandForm">
              修改搜尋條件
            </button>
          </div>
        </transition>

        <div
          class="school-search-form-anim-wrap"
          :class="{ 'school-search-form-anim-wrap--collapsed': formCollapsed }"
          :aria-hidden="formCollapsed ? 'true' : 'false'"
        >
        <form class="school-search-form" @submit.prevent="onSubmit">
          <div class="field">
            <label class="field-label" for="search-keyword">關鍵字</label>
            <input
              id="search-keyword"
              v-model="form.keyword"
              type="text"
              class="field-input"
              placeholder="輸入學校名稱或其他關鍵字"
              autocomplete="off"
            />
          </div>

          <div class="field-row">
            <div class="field field-half">
              <span class="field-label" id="search-districts-label">分區（可選多項）</span>
              <v-select
                aria-labelledby="search-districts-label"
                v-model="form.districts"
                :options="DISTRICTS"
                multiple
                placeholder="選擇分區"
                :close-on-select="false"
                class="school-search-vselect" 
                 max-height="200px"
              />
            </div>
            <div class="field field-half">
              <label class="field-label" for="search-school-type">學校類別</label>


              <v-select
                aria-labelledby="search-school-type-label"
                v-model="form.schoolType"
                :options="SCHOOL_TYPE_LABELS"
                placeholder="選擇學校類別"
                :close-on-select="true"
                :clearable="false"
                class="school-search-vselect"
              />





              </div>
          </div>

          <div class="field">
            <label class="field-label" for="search-service-category">服務類別</label>
            <v-select
              :disabled="serviceCategoryDisabled"
              v-model="form.serviceCategory"
              :options="SEN_SERVICE_CATEGORIES"
              placeholder="選擇服務類別"
              class="school-search-vselect"
              aria-describedby="search-service-category-help"
                :clearable="false"
            />
            <div id="search-service-category-help">
              <p v-if="serviceCategoryDisabled" class="school-search-field-hint">
                學校類別為幼稚園、小學、中學或國際學校時，服務類別<strong>不適用</strong>，此欄已停用；若先前有選擇會自動清除。
              </p>
              <p v-else class="school-search-field-hint">
                選擇服務類別後，<strong>學校類別</strong>會自動設為「特殊學校」。此選項只適用<strong>特殊學校</strong>；已選服務類別時，結果<strong>只會列出</strong>符合該類別的特殊學校，不會併入幼稚園／小學／中學列表。
              </p>
            </div>
          </div>

          <div class="field-row2">
            <div class="field field-half">
              <label class="field-label" for="search-gender">學生性別</label>
              <v-select
                v-model="form.gender"
                :options="GENDERS"
                placeholder="選擇學生性別"
                class="school-search-vselect"
                :clearable="false" 
                 max-height="100px"
              />
            </div>
            <div class="field field-half">
              <label class="field-label" for="search-religion">宗教</label>
              <v-select
                v-model="form.religion"
                :options="RELIGIONS"
                placeholder="選擇宗教"
                class="school-search-vselect"
                :clearable="false" 
                 max-height="120px"
              />
            </div>
          </div>

          <div class="school-search-actions">
            <button type="submit" class="btn btn-submit" :disabled="loading">
              {{ loading ? '搜尋中…' : '搜尋' }}
            </button>
            <button type="button" class="btn btn-reset" :disabled="loading" @click="onReset">重設</button>
          </div>
        </form>
        </div>

        <div v-if="searchMessage" class="school-search-notice">{{ searchMessage }}</div>
        <div v-if="searchError" class="school-search-error">{{ searchError }}</div>

        <section
          id="school-search-results"
          v-if="hasResults || searched"
          class="school-search-results"
        >
          <p v-if="searched && !loading" class="results-meta">
            共 <strong>{{ resultTotal }}</strong> 間
          </p>
          <div v-if="loading" class="results-loading">載入中…</div>
          <div v-else-if="hasResults" ref="resultsContainer" class="product-details-list view-switch school-search-list-block">
            <div class="school-list-container">
              <div class="school-list-header">
                <h2>進階搜尋<span class="comments">（{{ resultTotal }}間）</span></h2>
              </div>
              <div class="table-responsive">
                <table class="table table-striped table-bordered school-list-table">
                  <colgroup>
                    <col class="col1" />
                    <col class="col2" />
                    <col class="col3" />
                    <col class="col4" />
                    <col class="col5" />
                    <col class="col6" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">中文名稱</th>
                      <th scope="col">分區</th>
                      <th scope="col" class="col-sm-show">地址</th>
                      <th scope="col" class="col-md-show">聯絡電話</th>
                      <th scope="col" class="col-lg-show">資助種類</th>
                      <th scope="col" class="col-xl-show">性別</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, index) in pagedResults"
                      :key="row.level + '-' + row.id"
                      :class="{ 'row-even': index % 2 === 0, 'row-odd': index % 2 !== 0 }"
                    >
                      <td>
                        <a :href="schoolDetailPath(row)">{{ row.中文名稱 }}</a>
                      </td>
                      <td>{{ row.分區 }}</td>
                      <td class="col-sm-show">{{ row.地址 }}</td>
                      <td class="col-md-show">{{ row.聯絡電話 }}</td>
                      <td class="col-lg-show">{{ row.資助種類 }}</td>
                      <td class="col-xl-show">{{ row.就讀學生性別 }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="totalPages > 1" class="pagination-wrapper">
                <nav class="custom-pagination">
                  <ul class="pagination">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" @click.prevent="goToPage(1)">«</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                      <a class="page-link" @click.prevent="goToPage(currentPage - 1)">‹</a>
                    </li>
                    <li
                      v-for="page in visiblePages"
                      :key="page"
                      class="page-item"
                      :class="{ active: page === currentPage }"
                    >
                      <a class="page-link" @click.prevent="goToPage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" @click.prevent="goToPage(currentPage + 1)">›</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                      <a class="page-link" @click.prevent="goToPage(totalPages)">»</a>
                    </li>
                  </ul>
                </nav>
                <div v-if="results.length > 0" class="pagination-info">
                  顯示第 {{ (currentPage - 1) * perPage + 1 }} 至
                  {{ Math.min(currentPage * perPage, results.length) }} 條記錄，共 {{ results.length }} 條
                </div>
              </div>
            </div>
          </div>

          <p v-else-if="searched" class="results-empty">沒有符合條件的學校。</p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
const DISTRICTS = [
  '離島區',
  '中西區',
  '香港東區',
  '香港南區',
  '灣仔區',
  '九龍城區',
  '觀塘區',
  '西貢區',
  '深水埗區',
  '黃大仙區',
  '油尖旺區',
  '北區',
  '沙田區',
  '大埔區',
  '元朗區',
  '葵青區',
  '荃灣區',
  '屯門區',
];

const SCHOOL_TYPE_LABELS = ['不限', '幼稚園', '小學', '中學', '國際學校', '特殊學校'];

/** 選此類學校類別時停用「服務類別」（與後端不查 SEN 一致） */
const MAINSTREAM_SCHOOL_TYPES_NO_SEN = ['幼稚園', '小學', '中學', '國際學校'];

const RELIGIONS = [
  '不限',
  '佛教',
  '基督教',
  '不適用',
  '天主教',
  '道教',
  '孔教',
  '伊斯蘭教',
  '儒、釋、道',
  '道、釋、儒',
  '儒釋道',
];

const TRI_STATE_LABELS = ['不限', '有', '沒有'];

const GENDERS = ['不限', '男', '女', '男女'];

/** 與後端 SchoolAdvancedSearchService::SEN_SERVICE_CATEGORIES 一致 */
const SEN_SERVICE_CATEGORIES = [
  '不限',
  '視障兒童學校',
  '聽障兒童學校',
  '肢體傷殘兒童學校',
  '輕度智障兒童學校',
  '中度智障兒童學校',
  '輕中度智障兒童學校',
  '嚴重智障兒童學校',
  '群育學校',
  '醫院學校',
];

function defaultForm() {
  return {
    keyword: '',
    districts: [],
    schoolType: '不限',
    serviceCategory: '不限',
    gender: '不限',
    religion: '不限',
    incorporatedManagementCommittee: '不限',
    parentTeacherAssociation: '不限',
    studentUnion: '',
    alumniAssociation: '',
  };
}

export default {
  name: 'SchoolSearchPage',
  data() {
    return {
      DISTRICTS,
      SEN_SERVICE_CATEGORIES,
      form: defaultForm(),
      loading: false,
      searched: false,
      results: [],
      resultTotal: 0,
      currentPage: 1,
      perPage: 20,
      searchMessage: '',
      searchError: '',
      formCollapsed: false,
      triStateOptions: [
        { value: '', text: '不限' },
        { value: '有', text: '有' },
        { value: '沒有', text: '沒有' },
      ],
      SCHOOL_TYPE_LABELS,
      GENDERS,
      RELIGIONS,
      TRI_STATE_LABELS,
    };
  },
  computed: {
    hasResults() {
      return Array.isArray(this.results) && this.results.length > 0;
    },
    totalPages() {
      if (!this.results.length) {
        return 0;
      }
      return Math.ceil(this.results.length / this.perPage);
    },
    pagedResults() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.results.slice(start, start + this.perPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    formSummaryBrief() {
      const f = this.form;
      const parts = [];
      const kw = (f.keyword || '').trim();
      if (kw) {
        parts.push('關鍵字：「' + kw + '」');
      }
      const d = Array.isArray(f.districts) ? f.districts.filter(Boolean) : [];
      if (d.length === 1) {
        parts.push('分區：' + d[0]);
      } else if (d.length === 2) {
        parts.push('分區：' + d.join('、'));
      } else if (d.length > 2) {
        parts.push('分區：' + d.slice(0, 2).join('、') + ' 等 ' + d.length + ' 項');
      }
      if (f.schoolType && f.schoolType !== '不限') {
        parts.push('類別：' + f.schoolType);
      }
      if (f.serviceCategory && !this.serviceCategoryDisabled && f.serviceCategory !== '不限') {
        parts.push('服務類別：' + f.serviceCategory);
      }
      if (f.gender && f.gender !== '不限') {
        parts.push('性別：' + f.gender);
      }
      if (f.religion && f.religion !== '不限') {
        parts.push('宗教：' + f.religion);
      }
      if (f.incorporatedManagementCommittee && f.incorporatedManagementCommittee !== '不限') {
        parts.push('法團校董會：' + f.incorporatedManagementCommittee);
      }
      if (f.parentTeacherAssociation && f.parentTeacherAssociation !== '不限') {
        parts.push('家長教師會：' + f.parentTeacherAssociation);
      }
      if (f.studentUnion) {
        parts.push('學生會：' + f.studentUnion);
      }
      if (f.alumniAssociation) {
        parts.push('舊生會／校友會：' + f.alumniAssociation);
      }
      if (parts.length === 0) {
        return '目前條件：已套用篩選（未填寫關鍵字與選項）';
      }
      return '目前條件：' + parts.join(' · ');
    },
    serviceCategoryDisabled() {
      const t = this.form.schoolType || '';
      return MAINSTREAM_SCHOOL_TYPES_NO_SEN.indexOf(t) !== -1;
    },
  },
  watch: {
    'form.schoolType'() {
      if (this.serviceCategoryDisabled) {
        this.form.serviceCategory = '';
      }
    },
    'form.serviceCategory'(val) {
      if ((val || '').trim() !== '' && val !== '不限') {
        this.form.schoolType = '特殊學校';
      }
    },
  },
  methods: {
    expandForm() {
      this.formCollapsed = false;
    },
    scrollToResultsSection(delayMs) {
      const run = () => {
        const el =
          (this.$el && this.$el.querySelector && this.$el.querySelector('#school-search-results')) ||
          document.getElementById('school-search-results');
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };
      const d = typeof delayMs === 'number' ? delayMs : 0;
      this.$nextTick(() => {
        if (d > 0) {
          window.setTimeout(run, d);
        } else {
          run();
        }
      });
    },
    schoolDetailPath(item) {
      if (!item || !item.中文名稱) {
        return '/';
      }
      const name = item.中文名稱;
      if (item.學校類型 === '國際學校') {
        return '/int/' + encodeURIComponent(name);
      }
      return '/' + name;
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }
      this.currentPage = page;
      this.$nextTick(() => {
        const target = this.$refs.resultsContainer;
        if (!target) return;
        const rect = target.getBoundingClientRect();
        const offset = window.pageYOffset + rect.top - 20;
        window.scrollTo({ top: offset, behavior: 'smooth' });
      });
    },
    async onSubmit() {
      this.searchError = '';
      this.searchMessage = '';
      const payload = { ...this.form, districts: [...this.form.districts] };
      if (this.serviceCategoryDisabled) {
        payload.serviceCategory = '';
      }
      if (payload.schoolType === '不限') {
        payload.schoolType = '';
      }
      if (payload.serviceCategory === '不限') {
        payload.serviceCategory = '';
      }
      if (payload.gender === '不限') {
        payload.gender = '';
      }
      if (payload.religion === '不限') {
        payload.religion = '';
      }
      if (payload.incorporatedManagementCommittee === '不限') {
        payload.incorporatedManagementCommittee = '';
      }
      if (payload.parentTeacherAssociation === '不限') {
        payload.parentTeacherAssociation = '';
      }
      this.loading = true;
      this.searched = true;
      try {
        const { data } = await window.axios.post('/school-search', payload);
        this.results = data.data || [];
        this.resultTotal = data.total != null ? data.total : this.results.length;
        this.currentPage = 1;
        this.searchMessage = data.message || '';
        if (this.results.length > 0) {
          this.formCollapsed = true;
          // 略延遲捲動，讓表單收合動畫先播放
          this.scrollToResultsSection(400);
        }
      } catch (e) {
        this.results = [];
        this.resultTotal = 0;
        this.currentPage = 1;
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          (e.response && e.response.status === 422 && '請檢查輸入資料。') ||
          '搜尋失敗，請稍後再試。';
        this.searchError = typeof msg === 'string' ? msg : '搜尋失敗，請稍後再試。';
      } finally {
        this.loading = false;
      }
    },
    onReset() {
      this.form = defaultForm();
      this.results = [];
      this.searched = false;
      this.resultTotal = 0;
      this.currentPage = 1;
      this.formCollapsed = false;
      this.searchMessage = '';
      this.searchError = '';
    },
  },
};
</script>

<style scoped lang="scss">
.school-search-page {
  min-height: 60vh;
  color: #2f3540;
  padding-top: 100px;
}

.school-search-container {
  max-width: 1680px;
  width: 100%;
  padding: 0 20px 60px;
}

.school-search-header {
  text-align: center;
  margin-bottom: 2rem;
}

/* 表單收合：高度收斂 + 淡出 + 略向上移（配合頁面捲動） */
.school-search-form-anim-wrap {
  overflow: hidden;
  max-height: 3200px;
  opacity: 1;
  transform: translateY(0);
  transition:
    max-height 0.45s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.38s ease,
    transform 0.42s cubic-bezier(0.4, 0, 0.2, 1);
}

.school-search-form-anim-wrap--collapsed {
  max-height: 0;
  opacity: 0;
  transform: translateY(-16px);
  pointer-events: none;
  margin-top: 0;
  margin-bottom: 0;
}

/* 摘要列進場：由上滑入並淡入 */
.school-search-bar-enter-active,
.school-search-bar-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.38s cubic-bezier(0.4, 0, 0.2, 1);
}

.school-search-bar-enter,
.school-search-bar-leave-to {
  opacity: 0;
  transform: translateY(-14px);
}

.school-search-bar-enter-to,
.school-search-bar-leave {
  opacity: 1;
  transform: translateY(0);
}

.school-search-lead {
  max-width: 42rem;
  margin: 0.75rem auto 0;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.6;
}

.school-search-form-collapsed {
  max-width: 720px;
  margin: 0 auto 1.25rem;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
  padding: 1rem 1.25rem;
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(12, 31, 56, 0.06);
}

.school-search-form-collapsed-summary {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  line-height: 1.55;
  color: #374151;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.btn-expand-form {
  flex-shrink: 0;
  display: inline-block;
  padding: 10px 18px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  border-radius: 8px;
  border: 1px solid #007bff;
  color: #007bff;
  background: #fff;
  cursor: pointer;
  transition: all 0.18s ease;
}

.btn-expand-form:hover {
  background: #007bff;
  color: #fff;
}

@media (max-width: 576px) {
  .school-search-form-collapsed {
    padding: 0.75rem 0.85rem;
    gap: 0.4rem 0.5rem;
  }

  .school-search-form-collapsed-summary {
    font-size: 13px;
  }

  .btn-expand-form {
    padding: 8px 12px;
    font-size: 13px;
  }
}

.school-search-form {
  max-width: 720px;
  margin: 0 auto;
  text-align: left;
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 12px;
  padding: 1.5rem 1.5rem 1.25rem;
  box-shadow: 0 8px 24px rgba(12, 31, 56, 0.06);
}

.field {
  margin-bottom: 1.35rem;
  padding-bottom: 20px;
}

.school-search-field-hint {
  margin: 0.35rem 0 0;
  font-size: 1.2rem;
  line-height: 1.45;
  color: #6b7280;
}

.field-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 16px;
  line-height: 1.45;
  color: #243447;
}

.field-input,
.field-select {
  display: block;
  width: 100%;
  max-width: 100%;
  padding: 10px 12px;
  font-size: 16px;
  line-height: 1.45;
  color: #1f2933;
  background: #fff;
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  box-sizing: border-box;
  min-height: 44px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.field-select {
  height: 44px;
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, #64748b 50%),
    linear-gradient(135deg, #64748b 50%, transparent 50%);
  background-position: calc(100% - 17px) calc(50% - 2px), calc(100% - 12px) calc(50% - 2px);
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-right: 32px;
}

.field-select:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  background-color: #f3f4f6;
}

.field-input:focus,
.field-select:focus {
  outline: 0;
  border-color: #4c8bf5;
  box-shadow: 0 0 0 3px rgba(76, 139, 245, 0.14);
}

.field-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.25rem;
  margin-bottom: 0.5rem; 
}

.field-row2 {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.25rem;
  margin-bottom: 0.5rem; 
  padding-bottom: 50px;
}

.field-half {
  flex: 1 1 260px;
  margin-bottom: 1rem;
}

.school-search-actions {
  margin-top: 1.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e8e8e8;
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 0.75rem;
}

.school-search-actions .btn {
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  box-sizing: border-box;
}

.btn {
  display: inline-block;
  padding: 10px 18px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.5;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.18s ease;
}

.btn-submit {
  color: #fff;
  background: #007bff;
  border-color: #007bff;
}

.btn-submit:hover {
  background: #0069d9;
  border-color: #0062cc;
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(0, 98, 204, 0.22);
}

.btn-reset {
  color: #333;
  background: #fff;
  border-color: #ccc;
}

.btn-reset:hover {
  background: #f0f0f0;
}

/* vue-select 2.x：主容器為 .dropdown-toggle（非 v3 的 .vs__dropdown-toggle） */
/*
.school-search-vselect ::v-deep .dropdown-toggle {
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  min-height: 44px;
  padding: 4px 0 8px 0;
  background: #fff;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}


.school-search-vselect ::v-deep .selected-tag {
  margin-top: 3px;
  font-size: 16px;
}

.school-search-vselect ::v-deep input[type='search']::placeholder {
  color: #6c757d;
}

.school-search-vselect ::v-deep input[type='search'],
.school-search-vselect ::v-deep .selected-tag,
.school-search-vselect ::v-deep .open-indicator:before {
  font-size: 16px;
}

.school-search-vselect ::v-deep .dropdown-toggle:focus-within,
.school-search-vselect ::v-deep.open .dropdown-toggle {
  border-color: #4c8bf5;
  box-shadow: 0 0 0 3px rgba(76, 139, 245, 0.14);
}

@media (max-width: 768px) {
  .school-search-form {
    padding: 1.1rem 1rem;
    border-radius: 10px;
  }

  .school-search-actions {
    gap: 0.5rem;
  }

  .school-search-actions .btn {
    padding: 10px 12px;
    font-size: 14px;
  }

  .field-label,
  .field-input,
  .field-select,
  .school-search-vselect ::v-deep input[type='search'],
  .school-search-vselect ::v-deep .selected-tag {
    font-size: 15px;
  }
}
*/
.school-search-notice {
  max-width: 720px;
  margin: 1rem auto 0;
  padding: 0.75rem 1rem;
  background: #f0f7ff;
  border: 1px solid #c5daf7;
  border-radius: 8px;
  color: #1a4a7a;
  font-size: 15px;
}

.school-search-error {
  max-width: 720px;
  margin: 1rem auto 0;
  padding: 0.75rem 1rem;
  background: #fff5f5;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  color: #721c24;
  font-size: 15px;
}

.school-search-results {
  max-width: 1680px;
  width: 100%;
  margin: 2rem auto 0;
  padding: 0 0 2rem;
}

.school-search-list-block {
  margin-top: 0.5rem;
}

.results-meta {
  font-size: 15px;
  color: #555;
  margin: 0 0 1rem;
}

.results-loading,
.results-empty {
  font-size: 15px;
  color: #666;
  padding: 1rem 0;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
</style>



// WEBPACK FOOTER //
// resources/js/school-search/SchoolSearchPage.vue